import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { TokenService } from './token.service';

export interface EmailFolder {
  id: number;
  name: string;
  parent_id: number | null;
  created_at: string;
  updated_at: string;
  has_children: boolean;
  children?: EmailFolder[];
  email_count?: number;
}

@Injectable({ providedIn: 'root' })
export class FolderService {
  constructor(private httpClient: HttpClient, private tokenService: TokenService) { }

  // Add this method to folder.service.ts
  public getAllFolders(): Observable<{
    folders: EmailFolder[],
    breadcrumb: any[], // you can create a proper type if needed
    current_folder_id: number | null
  }> {
    return this.httpClient.get<{
      folders: EmailFolder[],
      breadcrumb: any[],
      current_folder_id: number | null
    }>(`${environment.backendUrl}/api/email_folders/`);
  }


  // Get folders
  public getFolders(folderId: string): Observable<{
    folders: EmailFolder[],
    breadcrumb: any[], // you can create a proper type if needed
    current_folder_id: number | null
  }> {
    let params = {};
    if (folderId !== null) {
      params = { parent_id: folderId };
    }
    return this.httpClient.get<{
      folders: EmailFolder[],
      breadcrumb: any[],
      current_folder_id: number | null
    }>(`${environment.backendUrl}/api/email_folders/`, { params });
  }

  // Get folder contents (both subfolders and emails)
  public getFolderContents(folderId: number | 'root'): Observable<any> {
    return this.httpClient.get<any>(`${environment.backendUrl}/api/folder_contents/${folderId}/`);
  }

  // Create folder
  public createFolder(name: string, parentId: number | 'root' | null = null): Observable<EmailFolder> {
    const data: any = { name };
    if (parentId !== null) {
      data.parent_id = parentId;
    }
    return this.httpClient.post<EmailFolder>(`${environment.backendUrl}/api/email_folders/`, data);
  }

  // Update folder
  public updateFolder(id: number, data: { name?: string; parent_id?: number | null }): Observable<EmailFolder> {
    return this.httpClient.put<EmailFolder>(`${environment.backendUrl}/api/email_folders/${id}/`, data);
  }

  // Delete folder
  public deleteFolder(id: number): Observable<any> {
    return this.httpClient.delete(`${environment.backendUrl}/api/email_folders/${id}/`);
  }

  // Move email to folder
  public moveEmailToFolder(emailId: number, folderId: number | null, name: string, is_moving_folder: boolean): Observable<any> {
    return this.httpClient.post(`${environment.backendUrl}/api/generatesave/`, {
      id: emailId,
      folder_id: folderId,
      name: name,
      is_moving_folder: is_moving_folder
    });
  }

  // Check previews for a list of emails (Streaming)
  public checkPreviews(ids: number[]): Observable<any> {
    return new Observable(observer => {
      this.tokenService.verify().subscribe(isValid => {
        if (!isValid) {
          observer.error('Token invalid');
          return;
        }

        const token = this.tokenService.currentToken?.getPayload()?.access_token || '';

        fetch(`${environment.backendUrl}/api/folder_contents/check_previews/`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ ids })
        }).then(response => {
          if (!response.body) {
            observer.error('No response body');
            return;
          }
          const reader = response.body.getReader();
          const decoder = new TextDecoder();

          const processStream = ({ done, value }: { done: boolean, value?: Uint8Array }) => {
            if (done) {
              observer.complete();
              return;
            }

            const chunk = decoder.decode(value, { stream: true });
            const lines = chunk.split('\n');

            lines.forEach(line => {
              if (line.trim()) {
                try {
                  const result = JSON.parse(line);
                  observer.next(result);
                } catch (e) {
                  console.error('Error parsing stream line:', line, e);
                }
              }
            });

            reader.read().then(processStream).catch(err => observer.error(err));
          };

          reader.read().then(processStream).catch(err => observer.error(err));

        }).catch(err => observer.error(err));
      });
    });
  }
}
